import { useContext, useMemo } from 'react'

import { ActiveTenantContext } from '../../context/common/Context'
import { useAuth } from '../useAuth'

/**
 * Service constructor signature types for better type safety
 */
export type TenantRequiredService<T = unknown> = new (
  token: string,
  tenantId?: string,
  tenantType?: string
) => T
export type TenantOptionalService<T = unknown> = new (
  token: string,
  tenantId?: string,
  tenantType?: string
) => T
export type GlobalService<T = unknown> = new (token: string) => T

/**
 * Configuration for tenant-aware services
 */
export interface TenantServiceOptions {
  includeTenantType?: boolean
}

/**
 * Factory for services that require tenantId (like TenantService)
 *
 * @example
 * ```tsx
 * const getTenantService = useTenantRequiredService(TenantService)
 * const service = await getTenantService() // Works even if tenantId not available
 * ```
 */
export const useTenantRequiredService = <T>(ServiceClass: TenantRequiredService<T>) => {
  const { getAccessTokenSilently } = useAuth()
  const { tenantId, tenant } = useContext(ActiveTenantContext)

  return useMemo(() => {
    return async (): Promise<T> => {
      const token = await getAccessTokenSilently()
      return new ServiceClass(token, tenantId, tenant?.type)
    }
  }, [getAccessTokenSilently, tenantId, tenant?.type])
}

/**
 * Factory for services that optionally use tenant info (most ApiService subclasses)
 *
 * @example
 * ```tsx
 * // Include tenantType (default)
 * const getService = useTenantOptionalService(BookingService)
 *
 * // Exclude tenantType
 * const getService = useTenantOptionalService(SettingsService, { includeTenantType: false })
 * ```
 */
export const useTenantOptionalService = <T>(
  ServiceClass: TenantOptionalService<T>,
  options: TenantServiceOptions = {}
) => {
  const { getAccessTokenSilently } = useAuth()
  const { tenantId, tenant } = useContext(ActiveTenantContext)
  const { includeTenantType = true } = options

  return useMemo(() => {
    return async (): Promise<T> => {
      const token = await getAccessTokenSilently()
      return new ServiceClass(token, tenantId, includeTenantType ? tenant?.type : undefined)
    }
  }, [getAccessTokenSilently, tenantId, tenant?.type, includeTenantType])
}

/**
 * Factory for global services that don't need tenant info (like UserService)
 *
 * @example
 * ```tsx
 * const getUserService = useGlobalService(UserService)
 * const service = await getUserService()
 * ```
 */
export const useGlobalService = <T>(ServiceClass: GlobalService<T>) => {
  const { getAccessTokenSilently } = useAuth()

  return useMemo(() => {
    return async (): Promise<T> => {
      const token = await getAccessTokenSilently()
      return new ServiceClass(token)
    }
  }, [getAccessTokenSilently])
}

/**
 * Legacy factory - maintains backward compatibility
 * @deprecated Use the specific factory functions above for better type safety
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const useServiceFactory = <T extends new (...args: any[]) => any>(
  ServiceClass: T,
  options: { includeTenantId?: boolean; includeTenantType?: boolean; requireTenant?: boolean } = {}
) => {
  const { includeTenantId = true, includeTenantType = true, requireTenant = true } = options

  if (!includeTenantId && !includeTenantType && !requireTenant) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useGlobalService(ServiceClass as GlobalService<T>)
  }

  if (requireTenant && includeTenantId) {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    return useTenantRequiredService(ServiceClass as TenantRequiredService<T>)
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useTenantOptionalService(ServiceClass as TenantOptionalService<T>, { includeTenantType })
}
